import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import {
  RefreshCw,
  Check,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useGetAIModels } from "../../api/queryHooks";
import CustomTooltip from "../Tooltip/CustomTooltip";
import useModelPreferences from "../../hooks/useModelPreferences";
import { useQuery } from "@tanstack/react-query";
import { getProfilePreferences } from "../../api/preferences";
import { getFileModelCompatibilityError } from "../../utils/fileModelCompatibility";

const RegenerateDropdown = ({
  chatId,
  onRegenerate,
  setRegenerateIndex,
  index,
  modelSelected,
  regenerateIndex,
  styleId,
  styleVersionId,
  styleSelectionMode,
  attachments = [],
}) => {
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { getEnabledModels } = useModelPreferences();
  const { data: allModels, isLoading } = useGetAIModels();
  const options = getEnabledModels(allModels);
  const { data: profile } = useQuery({
    queryKey: ["profile-preferences"],
    queryFn: getProfilePreferences,
    staleTime: 300000,
  });
  const styles = profile?.styles || [];
  const sortedStyles = [...styles].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" })
  );
  const activeStyle = styles.find((style) => style.style_id === profile?.active_style_id);

  const handleModelSelect = async (model) => {
    setRegenerateIndex(index);
    setIsOpen(false);
    const currentSelection = styleId
      ? { mode: "style", style_id: styleId }
      : { mode: styleSelectionMode === "none" ? "none" : "profile_default" };
    await onRegenerate(chatId, model.id, currentSelection, styleVersionId || null);
  };

  const handleStyleSelect = async (styleSelection) => {
    setRegenerateIndex(index);
    setIsOpen(false);
    await onRegenerate(chatId, modelSelected, styleSelection, null);
  };

  useClickOutside([dropdownRef, buttonRef], () => setIsOpen(false));

  useEffect(() => {
    const handleScrollOutside = (event) => {
      const path = event.composedPath ? event.composedPath() : [];

      const isInsideDropdown = path.includes(dropdownRef.current);
      const isInsideButton = path.includes(buttonRef.current);

      if (!isInsideDropdown && !isInsideButton) {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScrollOutside, true);

    return () => {
      window.removeEventListener("scroll", handleScrollOutside, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const computePosition = () => {
      if (!buttonRef.current || !dropdownRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const margin = 8;
      const gap = 2;
      const above = Math.max(0, buttonRect.top - gap - margin);
      const below = Math.max(0, window.innerHeight - buttonRect.bottom - gap - margin);
      const dropdown = dropdownRef.current;
      dropdown.style.maxHeight = `${Math.max(above, below)}px`;
      const height = dropdown.offsetHeight;
      const openAbove = height > below && above > below;
      dropdown.style.top = `${Math.max(margin, openAbove ? buttonRect.top - gap - height : buttonRect.bottom + gap)}px`;
      dropdown.style.left = `${Math.max(margin, Math.min(buttonRect.left, window.innerWidth - dropdown.offsetWidth - margin))}px`;
    };

    computePosition();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(computePosition);
    observer?.observe(dropdownRef.current);
    window.addEventListener("resize", computePosition);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", computePosition);
    };
  }, [isOpen]);

  return (
    <div ref={buttonRef} className="relative select-none">
      <CustomTooltip content="Regenerate response">
        <button
          type="button"
          aria-label="Regenerate response"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          disabled={Boolean(regenerateIndex)}
          className={`flex items-center gap-2 text-xs rounded-lg p-2 mr-3 dark:hover:bg-fill-icon-hover-dark/100 hover:bg-fill-icon-hover/25
          ${
            regenerateIndex ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }
          transition-transform duration-700`}
          onClick={() => {
            if (!regenerateIndex) {
              setIsOpen((prev) => !prev);
            }
          }}
        >
          <RefreshCw 
            className={`w-4 h-4 transition-transform duration-300 text-ink-ink dark:text-ink-white ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </button>
      </CustomTooltip>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            className="fixed overflow-y-auto w-[260px] max-w-[calc(100vw-16px)] z-[1000] rounded-xl bg-fill-white dark:bg-fill-panel-surface border border-edge-gray-200 dark:border-edge-panel-border shadow-lg backdrop-blur-sm"
          >
            <div className="px-4 py-3 text-[13px] text-ink-gray-700 dark:text-ink-gray-200 font-semibold border-b border-edge-border dark:border-edge-muted-control bg-fill-white dark:bg-fill-navigation-surface-dark/100">
              Rephrase this answer
            </div>
            <div className="max-h-[220px] overflow-y-auto show-scrollbar p-2 flex flex-col gap-1 border-b border-edge-gray-200 dark:border-edge-gray-700">
              {isLoading ? (
                <div className="px-4 py-2 text-ink-gray-400 text-sm text-center">
                  Loading models...
                </div>
              ) : options.length > 0 ? (
                options.map((model) => {
                  const isSelected = modelSelected === model.id;
                  const isIncompatible = attachments.some((file) =>
                    getFileModelCompatibilityError(file, model)
                  );
                  return (
                    <button
                      type="button"
                      role="menuitem"
                      key={model.id}
                      disabled={isIncompatible}
                      onClick={() => handleModelSelect(model)}
                      className={`min-h-[50px] w-full flex items-center px-3 py-1 text-left rounded-lg cursor-pointer group relative transition-all duration-200
                        ${isIncompatible ? "opacity-50 cursor-not-allowed" : ""}
                        ${
                          isSelected
                            ? "photon-control-active bg-fill-navigation-active border border-edge-indigo-100 dark:border-edge-gray-600"
                            : "hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700"
                        }`}
                    >
                      <div className="flex flex-col w-full justify-center">
                        <span
                          className={`truncate text-[12px] font-medium ${
                            isSelected
                              ? "text-inherit font-semibold"
                              : "text-ink-gray-900 dark:text-ink-gray-50"
                          }`}
                        >
                          {model.name}
                        </span>
                        <span className="text-ink-gray-500 dark:text-ink-gray-400 text-[10px] line-clamp-2 break-words leading-3 mt-1">
                          {model.description}
                        </span>
                      </div>
                      {isSelected ? (
                        <div className="flex items-center">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200">
                            <Check className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent group-hover:hidden" />
                            <RefreshCw className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent hidden group-hover:block" />
                          </div>
                        </div>
                      ) : null}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-2 text-ink-gray-400 text-sm text-center">
                  No models available
                </div>
              )}
            </div>
            <div className="p-2">
              <p className="px-2 py-1 text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400">Style</p>
              {[
                ...(activeStyle ? [{
                  key: "profile",
                  label: activeStyle.name,
                  value: { mode: "profile_default" },
                }] : []),
                ...sortedStyles.filter((style) => style.style_id !== activeStyle?.style_id).map((style) => ({ key: style.style_id, label: style.name, value: { mode: "style", style_id: style.style_id } })),
                { key: "none", label: "Photon default", value: { mode: "none" } },
              ].map((style) => (
                <button key={style.key} type="button" role="menuitem" onClick={() => handleStyleSelect(style.value)} className="w-full rounded px-3 py-2 text-left text-xs text-ink-gray-700 hover:bg-fill-gray-200 dark:text-ink-gray-200 dark:hover:bg-fill-gray-700">{style.label}</button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RegenerateDropdown;
